import { Dropdown, ELinkVariant, ETextStyleVariant, Link, Text } from '@outdoorsyco/bonfire';
import { clsx } from 'clsx';
import { useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { ESearchFilters } from '@/constants/searchFilters';
import { allVehicleStyles, vehicleTypesBonfire } from '@/constants/vehicleDetails';
import { EFilterMethod, setFormFilter } from '@/redux/modules/searchForm';
import { getQueryParams } from '@/redux/selectors/queryParams';
import { getSearchFormRVProfileFilter } from '@/redux/selectors/search/searchForm';
import { TVehicleType } from '@/services/types/search/rentals/id';

interface IRVProfileState {
  type?: TVehicleType;
  length?: number;
  slideouts?: string[];
}

const MIN_RV_LENGTH = 10;
const MAX_RV_LENGTH = 45;
const lengthOptions = Array(MAX_RV_LENGTH - MIN_RV_LENGTH + 1)
  .fill(0)
  .map((_, index) => index + MIN_RV_LENGTH);

interface IRVProfileFilterContainerProps {
  isInMoreFilter?: boolean;
}

const RVProfileFilter = ({ isInMoreFilter }: IRVProfileFilterContainerProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const searchFormRVProfile = useSelector(getSearchFormRVProfileFilter);
  const filterSubTypesFromQueryParams =
    useSelector(getQueryParams)?.[ESearchFilters.CAMPSITE_SUPPORTED_SUBTYPES];
  const filterLengthFromQueryParams =
    useSelector(getQueryParams)?.[ESearchFilters.CAMPSITE_SUPPORTED_LENGTH];
  const filterSlideOutsFromQueryParams =
    useSelector(getQueryParams)?.[ESearchFilters.CAMPSITE_SUPPORTED_SLIDEOUTS];

  const profile = useMemo<IRVProfileState>(
    () => ({
      type: searchFormRVProfile[ESearchFilters.CAMPSITE_SUPPORTED_SUBTYPES]?.[0] as TVehicleType,
      length: searchFormRVProfile[ESearchFilters.CAMPSITE_SUPPORTED_LENGTH],
      slideouts: searchFormRVProfile[ESearchFilters.CAMPSITE_SUPPORTED_SLIDEOUTS],
    }),
    [searchFormRVProfile],
  );

  // update searchform in redux store when search from url or homepage
  useEffect(() => {
    if (
      typeof filterSubTypesFromQueryParams !== 'string' &&
      typeof filterLengthFromQueryParams !== 'string' &&
      typeof filterSlideOutsFromQueryParams !== 'string'
    ) {
      return;
    }

    dispatch(
      setFormFilter(
        {
          filters: {
            ...(typeof filterSubTypesFromQueryParams === 'string' && {
              [ESearchFilters.CAMPSITE_SUPPORTED_SUBTYPES]: [
                filterSubTypesFromQueryParams.split(',')[0],
              ] as TVehicleType[],
            }),
            ...(typeof filterLengthFromQueryParams === 'string' && {
              [ESearchFilters.CAMPSITE_SUPPORTED_LENGTH]: Number(filterLengthFromQueryParams),
            }),
            ...(typeof filterSlideOutsFromQueryParams === 'string' && {
              [ESearchFilters.CAMPSITE_SUPPORTED_SLIDEOUTS]:
                filterSlideOutsFromQueryParams.split(','),
            }),
          },
          filterMethod: null,
        },
        false,
      ),
    );

    // Run only on mount to sync state with query parameters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRVTypeChange = (type: TVehicleType) => {
    dispatch(
      setFormFilter({
        filters: { [ESearchFilters.CAMPSITE_SUPPORTED_SUBTYPES]: [type] },
        filterMethod: EFilterMethod.DROPDOWN,
      }),
    );
  };

  const handleLengthChange = (length: string) => {
    const lengthInt = parseInt(length);
    dispatch(
      setFormFilter({
        filters: { [ESearchFilters.CAMPSITE_SUPPORTED_LENGTH]: lengthInt },
        filterMethod: EFilterMethod.DROPDOWN,
      }),
    );
  };

  const handleSlideOutsChange = (slideouts: string[]) => {
    const targetValue = slideouts.at(-1); // Get the most recent
    const newSlideOuts =
      targetValue === 'no-slide-outs'
        ? ['no-slide-outs'] // If 'no-slide-outs' is selected, unselect all other options
        : slideouts.filter(item => item !== 'no-slide-outs'); // Remove 'no-slide-outs' if it's selected
    dispatch(
      setFormFilter({
        filters: { [ESearchFilters.CAMPSITE_SUPPORTED_SLIDEOUTS]: newSlideOuts },
        filterMethod: EFilterMethod.DROPDOWN,
      }),
    );
  };

  const rvTypeLabel = intl.formatMessage({ defaultMessage: 'RV type', id: 'k+qZB9' });
  const rvLengthLabel = intl.formatMessage({ defaultMessage: 'RV length', id: '9PlAE4' });
  const slideOutsLabel = intl.formatMessage({ defaultMessage: 'Slide-outs', id: 'UP+UBd' });

  return (
    <div className="flex flex-col">
      <div>
        {isInMoreFilter && (
          <Text variant={ETextStyleVariant.MediumBold}>
            <FormattedMessage
              defaultMessage="RV profile"
              id="Equq1B"
              description="RV Profile Filter Label"
            />
          </Text>
        )}
        <Text
          className="text-gray-50"
          variant={isInMoreFilter ? ETextStyleVariant.SmallRegular : undefined}>
          <FormattedMessage
            defaultMessage="Enter your RV profile details to only show sites that will accommodate your specific requirements."
            id="GfF74S"
          />
        </Text>
        <Text
          className="mt-2 text-gray-50"
          variant={isInMoreFilter ? ETextStyleVariant.SmallRegular : undefined}>
          <FormattedMessage
            id="MbdvCQ"
            defaultMessage="Don't have an RV yet? <link>Browse RV rentals</link>."
            description="Campground search > RV profile filter > Link to RV rentals search page"
            values={{
              link: text => (
                <Link href="/rv-search" variant={ELinkVariant.Underline}>
                  {text}
                </Link>
              ),
            }}
          />
        </Text>
      </div>

      <div className={clsx('flex flex-col gap-6', { 'py-4': !isInMoreFilter })}>
        <div className="flex flex-col mt-2 gap-y-2">
          <div className="flex flex-col gap-4">
            <Dropdown
              label={rvTypeLabel}
              ariaLabel={rvTypeLabel}
              inputValue={String(profile.type)}
              onChange={value => handleRVTypeChange(value as TVehicleType)}
              sheetClassName="z-[1410]">
              {allVehicleStyles.map(item => {
                const id = `filter-option-${item}`;
                const vehicleType = vehicleTypesBonfire[item];

                return (
                  <Dropdown.Option
                    key={id}
                    value={item}
                    content={{ label: intl.formatMessage(vehicleType.label) }}
                  />
                );
              })}
            </Dropdown>

            <Dropdown
              label={rvLengthLabel}
              ariaLabel={rvLengthLabel}
              inputValue={String(profile.length)}
              onChange={handleLengthChange}
              sheetClassName="z-[1410]">
              {lengthOptions.map((option, index) => {
                const key = String(option);
                let label = intl.formatMessage(
                  { defaultMessage: '{feet} feet', id: '6hX3Rn' },
                  { feet: option },
                );

                if (index === 0) {
                  label = intl.formatMessage(
                    {
                      defaultMessage: '{feet} feet or less',
                      id: '4g4WFK',
                    },
                    { feet: option },
                  );
                } else if (index === lengthOptions.length - 1) {
                  label = intl.formatMessage(
                    {
                      defaultMessage: '{feet} feet or more',
                      id: 'A7XOA2',
                    },
                    { feet: option },
                  );
                }

                return <Dropdown.Option key={key} value={key} content={{ label }} />;
              })}
            </Dropdown>

            <Dropdown
              ariaLabel={slideOutsLabel}
              clearSelectionOptions={{
                ariaLabel: 'Clear selection',
              }}
              inputValue={profile.slideouts || []}
              label={slideOutsLabel}
              multiple
              dropdownOnly
              onChange={handleSlideOutsChange}>
              <Dropdown.OptionGroup label="">
                <Dropdown.Option
                  content={{
                    label: intl.formatMessage({
                      defaultMessage: 'Driver',
                      id: 'MbbFzn',
                    }),
                  }}
                  value="driver"
                />
                <Dropdown.Option
                  content={{
                    label: intl.formatMessage({
                      defaultMessage: 'Passenger',
                      id: 'ZsCln4',
                    }),
                  }}
                  value="passenger"
                />
                <Dropdown.Option
                  content={{
                    label: intl.formatMessage({
                      defaultMessage: 'Rear',
                      id: 'Y1WiS9',
                    }),
                  }}
                  value="rear"
                />
                <Dropdown.Option
                  content={{
                    label: intl.formatMessage({
                      defaultMessage: 'No slide-outs',
                      id: 'Nx2RqW',
                    }),
                  }}
                  value="no-slide-outs"
                />
              </Dropdown.OptionGroup>
            </Dropdown>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RVProfileFilter;
